import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Receipt, CheckCircle2, Circle, CreditCard, FileDown, Landmark, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = window.__BACKEND_URL__;
const SYM = { USD: 'US$', CAD: 'CA$', EUR: '€', GBP: '£', AUD: 'A$', NZD: 'NZ$', MXN: 'MX$' };
const money = (n, cur) => `${SYM[cur] || `${cur || ''} `}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');

/** Estimate → invoice journey + invoice summary + pay options for the customer portal. */
export const PortalBilling = ({ token, brandColor }) => {
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = () => axios.get(`${API}/api/portal/${token}/billing`).then((r) => setData(r.data)).catch(() => setData({ stages: [], invoice: null, payment: {} }));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (params.get('paid') === '1' && sid) {
      axios.post(`${API}/api/portal/${token}/pay/confirm`, null, { params: { session_id: sid } })
        .then((r) => { if (r.data.paid) toast.success('Payment received — thank you!'); })
        .catch(() => {})
        .finally(load);
    } else {
      load();
    }
  }, [token]);

  const payNow = async () => {
    setPaying(true);
    try {
      const { data: d } = await axios.post(`${API}/api/portal/${token}/pay`);
      window.location.assign(d.checkout_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start payment');
      setPaying(false);
    }
  };

  if (!data) return null;
  const inv = data.invoice;
  const pay = data.payment || {};
  const canStripe = inv && inv.status !== 'paid' && pay.stripe_available && (pay.method === 'stripe' || pay.method === 'both');
  const showRemit = inv && inv.status !== 'paid' && (pay.method === 'invoice' || pay.method === 'both' || !pay.stripe_available);

  return (
    <>
      <Card data-testid="portal-journey-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Receipt className="w-4 h-4 text-slate-500" /> Your service journey</CardTitle></CardHeader>
        <CardContent>
          <ol className="space-y-2" data-testid="portal-journey-stages">
            {(data.stages || []).map((s) => (
              <li key={s.key} className="flex items-center gap-3 text-sm" data-testid={`portal-stage-${s.key}`}>
                {s.done ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: brandColor }} /> : <Circle className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                <span className={s.done ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400'}>{s.label}</span>
                {s.at && s.done && <span className="ml-auto text-xs text-slate-400">{fmt(s.at)}</span>}
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {inv && (
        <Card data-testid="portal-invoice-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><Receipt className="w-4 h-4 text-slate-500" /> Invoice {inv.number || ''}</span>
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold" data-testid="portal-invoice-status"
                style={inv.status === 'paid' ? { background: '#D1FAE5', color: '#065F46' } : { background: '#FEF3C7', color: '#92400E' }}>
                {inv.status === 'paid' ? `Paid ${fmt(inv.paid_at)}` : 'Awaiting payment'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {inv.presentation === 'lump_sum' ? (
              <p className="text-slate-700 dark:text-slate-300" data-testid="portal-invoice-summary">{inv.summary || 'Services as quoted.'}</p>
            ) : (
              <ul className="divide-y" data-testid="portal-invoice-lines">
                {(inv.lines || []).map((l, i) => (
                  <li key={i} className="flex justify-between py-1.5"><span>{l.description}</span><span>{money(l.amount, inv.currency)}</span></li>
                ))}
              </ul>
            )}
            <div className="border-t pt-2 space-y-1">
              <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>{money(inv.subtotal, inv.currency)}</span></div>
              {inv.tax_amount > 0 && <div className="flex justify-between text-slate-500"><span>{inv.tax_label || 'Tax'}</span><span>{money(inv.tax_amount, inv.currency)}</span></div>}
              <div className="flex justify-between text-lg font-bold"><span>Total ({inv.currency})</span><span data-testid="portal-invoice-total">{money(inv.grand_total, inv.currency)}</span></div>
            </div>
            {inv.note_to_customer && <p className="text-xs text-slate-500 whitespace-pre-wrap">{inv.note_to_customer}</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              {canStripe && (
                <Button onClick={payNow} disabled={paying} style={{ background: brandColor }} className="text-white" data-testid="portal-pay-now-btn">
                  {paying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1.5" />} Pay now
                </Button>
              )}
              <Button variant="outline" asChild data-testid="portal-invoice-pdf-btn">
                <a href={`${API}/api/portal/${token}/invoice.pdf`} target="_blank" rel="noreferrer"><FileDown className="w-4 h-4 mr-1.5" /> Download invoice (PDF)</a>
              </Button>
            </div>
            {showRemit && (
              <div className="rounded-lg border p-3 text-xs text-slate-600 dark:text-slate-300 flex gap-2" data-testid="portal-remit-to">
                <Landmark className="w-4 h-4 flex-shrink-0 text-slate-400" />
                <div>
                  <p className="font-semibold">Pay by invoice / bank transfer</p>
                  <p className="whitespace-pre-wrap">{pay.remit_to || 'Remit-to details are printed on the PDF invoice.'}</p>
                  {pay.terms && <p className="mt-1">Terms: {pay.terms}</p>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};
